/**
 * Canvas Event Handlers
 * Handlers for user interactions in the grid canvas
 */

import type { ContainerElement, TemplateElement } from '../types/template.types';
import { createNewElement } from './element-factory';
import { findParentContainerId, deleteElementFromContainer } from './container-operations';

/**
 * Creates handler for insert button clicks
 */
export function createInsertHandler(
  setPendingInsert: (insert: PendingInsert | null) => void,
  setDialogOpen: (open: boolean) => void
) {
  return (
    containerPath: number[],
    rowIndex: number,
    position: 'above' | 'below',
    cellRow?: number,
    cellColumn?: number
  ) => {
    console.log('Insert click:', { containerPath, rowIndex, position, cellRow, cellColumn });
    setPendingInsert({ containerPath, rowIndex, position, cellRow, cellColumn });
    setDialogOpen(true);
  };
}

/**
 * Creates handler for element clicks with drill-up pattern
 */
export function createElementClickHandler(
  containers: ContainerElement[],
  headerContainer: ContainerElement | undefined,
  footerContainer: ContainerElement | undefined,
  selectedElementId: string | null,
  onSelectElement: (elementId: string | null) => void
) {
  return (elementId: string | null) => {
    if (elementId === null) {
      onSelectElement(null);
      return;
    }

    // Drill-up pattern: if already selected, select parent container
    if (elementId === selectedElementId) {
      const parentId = findParentContainerId(elementId, containers, headerContainer, footerContainer);
      onSelectElement(parentId);
    } else {
      onSelectElement(elementId);
    }
  };
}

/**
 * Creates handler for element deletion
 */
export function createDeleteElementHandler(
  containers: ContainerElement[],
  onChange: (containers: ContainerElement[]) => void,
  onSelectElement: (elementId: string | null) => void
) {
  return (elementId: string) => {
    if (containers.length === 0) return;

    // Check if deleting a root container
    const containerIndex = containers.findIndex(c => c.id === elementId);
    if (containerIndex !== -1) {
      const newContainers = containers.filter((_, idx) => idx !== containerIndex);
      onChange(newContainers.length > 0 ? newContainers : []);
      onSelectElement(null);
      return;
    }

    // Otherwise, delete from within containers
    const newContainers = containers.map(container => 
      deleteElementFromContainer(container, elementId)
    );
    onChange(newContainers);
    onSelectElement(null);
  };
}

/**
 * Creates handler for scroll events
 */
export function createScrollHandler(
  setScrollPosition: (pos: { x: number; y: number }) => void
) {
  return (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setScrollPosition({
      x: target.scrollLeft,
      y: target.scrollTop
    });
  };
}

/**
 * Pending insert information
 */
export interface PendingInsert {
  containerPath: number[];
  rowIndex: number;
  position: 'above' | 'below';
  cellRow?: number;
  cellColumn?: number;
}

/**
 * Helper to get parent container from path
 */
/**
 * Helper to get target container from path (including last element)
 */
function getTargetContainer(
  containers: ContainerElement[],
  path: number[]
): ContainerElement | null {
  if (path.length === 0) return null;

  const firstIndex = path[0];
  if (firstIndex === undefined) return null;

  let currentContainer = containers[firstIndex];
  if (!currentContainer) return null;

  // If path has only one element, we found the target
  if (path.length === 1) return currentContainer;

  // Navigate deeper
  for (let i = 1; i < path.length; i++) {
    const childIndex = path[i];
    if (childIndex === undefined) return null;

    const children = Array.isArray(currentContainer.children) ? currentContainer.children : [];
    const child = children[childIndex];

    if (!child || child.element.type !== 'container') return null;
    currentContainer = child.element as ContainerElement;
  }

  return currentContainer;
}

/**
 * Limits element height to container constraints
 */
function limitElementHeight(
  element: TemplateElement,
  maxHeight: number
): TemplateElement {
  if (!('height' in element)) return element;

  const currentHeight = (element as { height?: number }).height;
  const currentWidth = (element as { width?: number }).width;

  if (!currentHeight || currentHeight <= maxHeight) {
    return currentHeight ? element : { ...element, height: maxHeight };
  }

  // Scale down proportionally
  const ratio = maxHeight / currentHeight;
  const updates: Partial<{ height: number; width: number }> = {
    height: maxHeight
  };
  
  if (currentWidth) {
    updates.width = Math.round(currentWidth * ratio);
  }
  
  return { ...element, ...updates };
}

/**
 * Inserts element into a specific cell
 */
function insertIntoCell(
  container: ContainerElement,
  path: number[],
  cellRow: number,
  cellColumn: number,
  element: TemplateElement
): ContainerElement {
  const children = Array.isArray(container.children) ? container.children : [];

  // If we've reached the target container
  if (path.length === 0) {
    const cellKey = `${cellRow}-${cellColumn}`;
    
    // Don't apply cell alignment from cellAlignments anymore
    // Element alignment should be set explicitly through PropertyGrid
    
    return {
      ...container,
      children: [
        ...children,
        { cellRow, cellColumn, element }
      ]
    };
  }

  // Navigate deeper
  const [currentIndex, ...restPath] = path;
  return {
    ...container,
    children: children.map((child, idx) => {
      if (idx === currentIndex && child.element.type === 'container') {
        return {
          ...child,
          element: insertIntoCell(
            child.element as ContainerElement,
            restPath,
            cellRow,
            cellColumn,
            element
          )
        };
      }
      return child;
    })
  };
}

/**
 * Creates handler for element type selection in insert dialog
 */
export function createSelectTypeHandler(
  containers: ContainerElement[],
  headerContainer: ContainerElement | undefined,
  footerContainer: ContainerElement | undefined,
  topMargin: number,
  bottomMargin: number,
  pendingInsert: PendingInsert | null,
  onChange: (containers: ContainerElement[]) => void,
  onHeaderChange: ((container: ContainerElement) => void) | undefined,
  onFooterChange: ((container: ContainerElement) => void) | undefined,
  setPendingInsert: (insert: PendingInsert | null) => void,
  setDialogOpen: (open: boolean) => void
) {
  return (type: string) => {
    if (!pendingInsert) return;

    // Standard A4-Abmessungen
    const A4_WIDTH = 794;
    const A4_MARGIN = 56.7;
    const PAGE_WIDTH = A4_WIDTH - (A4_MARGIN * 2); // ~680px
    const DEFAULT_HEIGHT = 400;

    // Erstelle neues Element mit Parent-Dimensionen
    let newElement = createNewElement(type, PAGE_WIDTH, DEFAULT_HEIGHT);

    // Root-Container: horizontal strecken, vertikal oben, feste Design-Höhe
    const isRootLevelInsert =
      // erster Container (noch keine Container vorhanden)
      containers.length === 0 ||
      // oder Einfügen auf Root-Ebene (kein cellRow / cellColumn)
      (pendingInsert.cellRow === undefined && pendingInsert.cellColumn === undefined);

    if (type === 'container' && isRootLevelInsert) {
      newElement = {
        ...newElement,
        horizontalAlign: 'stretch',
        verticalAlign: 'top',
        height: DEFAULT_HEIGHT,
      } as TemplateElement;
    }

    // Handle header container insert (direct or nested)
    if (pendingInsert.containerPath[0] === -1) {
      const { cellRow, cellColumn } = pendingInsert;
      if (cellRow === undefined || cellColumn === undefined || !headerContainer || !onHeaderChange) {
        console.error('Invalid header insert parameters');
        return;
      }

      newElement = limitElementHeight(newElement, headerContainer.height || topMargin);
      
      // Check if it's a nested container insert
      if (pendingInsert.containerPath.length > 1) {
        // Insert into nested container within header
        const updatedHeader = insertIntoCell(
          headerContainer,
          pendingInsert.containerPath.slice(1), // Remove the -1 prefix
          cellRow,
          cellColumn,
          newElement
        );
        onHeaderChange(updatedHeader);
      } else {
        // Direct insert into header root container
        const updatedHeader = {
          ...headerContainer,
          children: [
            ...(Array.isArray(headerContainer.children) ? headerContainer.children : []),
            { cellRow, cellColumn, element: newElement }
          ]
        };
        onHeaderChange(updatedHeader);
      }

      setPendingInsert(null);
      setDialogOpen(false);
      return;
    }

    // Handle footer container insert (direct or nested)
    if (pendingInsert.containerPath[0] === -2) {
      const { cellRow, cellColumn } = pendingInsert;
      if (cellRow === undefined || cellColumn === undefined || !footerContainer || !onFooterChange) {
        console.error('Invalid footer insert parameters');
        return;
      }

      newElement = limitElementHeight(newElement, footerContainer.height || bottomMargin);
      
      // Check if it's a nested container insert
      if (pendingInsert.containerPath.length > 1) {
        // Insert into nested container within footer
        const updatedFooter = insertIntoCell(
          footerContainer,
          pendingInsert.containerPath.slice(1), // Remove the -2 prefix
          cellRow,
          cellColumn,
          newElement
        );
        onFooterChange(updatedFooter);
      } else {
        // Direct insert into footer root container
        const updatedFooter = {
          ...footerContainer,
          children: [
            ...(Array.isArray(footerContainer.children) ? footerContainer.children : []),
            { cellRow, cellColumn, element: newElement }
          ]
        };
        onFooterChange(updatedFooter);
      }

      setPendingInsert(null);
      setDialogOpen(false);
      return;
    }

    // Handle first container
    if (containers.length === 0) {
      if (type === 'container') {
        onChange([newElement as ContainerElement]);
      }
      setPendingInsert(null);
      setDialogOpen(false);
      return;
    }

    // Handle root level insert
    if (pendingInsert.cellRow === undefined && pendingInsert.cellColumn === undefined) {
      if (type !== 'container') {
        alert('Auf dieser Ebene können nur Container platziert werden. Füge Elemente in die Container-Zellen ein.');
        return;
      }

      const { rowIndex } = pendingInsert;
      const newContainers = [
        ...containers.slice(0, rowIndex),
        newElement as ContainerElement,
        ...containers.slice(rowIndex),
      ];
      onChange(newContainers);
      setPendingInsert(null);
      setDialogOpen(false);
      return;
    }

    // Handle cell insert
    const { containerPath, cellRow, cellColumn } = pendingInsert;
    if (cellRow === undefined || cellColumn === undefined) {
      console.error('cellRow or cellColumn is undefined');
      return;
    }

    // Limit element height based on target container
    const targetContainer = getTargetContainer(containers, containerPath);
    if (targetContainer && 'height' in newElement) {
      const gap = targetContainer.gap ?? 8;
      const rows = targetContainer.rows || 1;
      const containerHeight = targetContainer.height || 200;
      const cellHeight = (containerHeight - (rows - 1) * gap) / rows;
      newElement = limitElementHeight(newElement, cellHeight);
    }

    // Insert into container
    const containerIndex = containerPath[0];
    const updatedContainers = containers.map((container, idx) =>
      idx === containerIndex
        ? insertIntoCell(container, containerPath.slice(1), cellRow, cellColumn, newElement)
        : container
    );

    // Save without nested height recalculation
    onChange(updatedContainers);

    setPendingInsert(null);
    setDialogOpen(false);
  };
}
